<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useD4HStore } from '@/stores/d4h'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import { D4H_REGIONS, type D4HRegion } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosChip from '@/components/ui/FosChip.vue'
import D4HQualMappingDialog from './D4HQualMappingDialog.vue'

defineProps<{
  agencyId: string
  // Agency.custom_quals, offered alongside STANDARD_MISC_QUALS
  customQuals?: string[]
}>()

const d4hStore = useD4HStore()
const syncStore = useSyncStore()
const toast = useToastStore()

const region = ref<D4HRegion>('us')
const apiToken = ref('')
const subdomain = ref('')
const selectedTeamId = ref<string | undefined>(undefined)
const saving = ref(false)
const loadingQuals = ref(false)
const loadError = ref<string | null>(null)
const mappingOpen = ref(false)

const offline = computed(() => !syncStore.isOnline)
const configured = computed(() => d4hStore.config?.configured ?? false)
const summary = computed(() => d4hStore.config?.last_sync_summary ?? null)
const lastSyncLabel = computed(() => {
  const at = d4hStore.config?.last_sync_at
  return at ? new Date(at).toLocaleString() : 'Never'
})

const mappedCount = computed(() => {
  const mapping = d4hStore.config?.qual_mapping ?? {}
  return d4hStore.qualifications.filter((q) => mapping[q.title]).length
})

async function loadQuals() {
  loadingQuals.value = true
  try {
    await d4hStore.loadQualifications()
  } catch (err) {
    toast.add(err instanceof Error ? err.message : 'Could not load D4H qualifications', 'error', 4000)
  } finally {
    loadingQuals.value = false
  }
}

async function save() {
  saving.value = true
  try {
    const { needsTeam } = await d4hStore.saveConfig({
      region: region.value,
      api_token: apiToken.value.trim() || undefined,
      team_id: selectedTeamId.value ? Number(selectedTeamId.value) : null,
      // Only send once the stored value is loaded, so an unloaded field doesn't clear it
      subdomain: d4hStore.config ? subdomain.value.trim() : undefined,
    })
    if (needsTeam) {
      toast.add('This token can access several D4H teams. Pick one and save again.', 'default', 5000)
      return
    }
    apiToken.value = ''
    toast.add('D4H settings saved', 'success', 3000)
    if (d4hStore.qualifications.length === 0) await loadQuals()
  } catch (err) {
    toast.add(err instanceof Error ? err.message : 'Could not save D4H settings', 'error', 4000)
  } finally {
    saving.value = false
  }
}

async function runSync() {
  try {
    const result = await d4hStore.sync()
    toast.add(`Roster synced: ${result.total} members`, 'success', 3000)
  } catch (err) {
    toast.add(err instanceof Error ? err.message : 'D4H sync failed', 'error', 4000)
  }
}

watch(() => d4hStore.config?.region, (r) => {
  if (r) region.value = r
}, { immediate: true })

watch(() => d4hStore.config?.subdomain, (s) => {
  subdomain.value = s ?? ''
}, { immediate: true })

onMounted(async () => {
  if (offline.value) return
  try {
    await d4hStore.loadConfig()
    if (d4hStore.config?.configured) await loadQuals()
  } catch (err) {
    loadError.value = err instanceof Error ? err.message : 'Could not load D4H settings'
  }
})

</script>

<template>
  <div class="flex flex-col gap-4 mb-6">
    <div class="flex items-center justify-between gap-2">
      <label class="text-[10px] font-label uppercase tracking-widest text-outline">D4H Roster</label>
      <FosChip v-if="configured" variant="success">{{ d4hStore.config?.team_name || 'Connected' }}</FosChip>
    </div>

    <p v-if="offline" class="text-xs text-on-surface-variant font-mono">
      Offline. D4H settings and sync need a connection.
    </p>
    <p v-else-if="loadError" class="text-xs text-error font-mono">{{ loadError }}</p>

    <!-- Connection -->
    <div class="border border-border rounded p-3 flex flex-col gap-3">
      <div class="grid grid-cols-[8rem_1fr] gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">Region</label>
          <FosSelect
            :model-value="region"
            :options="D4H_REGIONS.map((r) => ({ label: r.toUpperCase(), value: r }))"
            :disabled="offline"
            @update:model-value="region = $event as D4HRegion"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest">API Token</label>
          <FosInput
            v-model="apiToken"
            type="password"
            :placeholder="configured ? 'Saved. Enter a new token to replace it' : 'D4H personal access token'"
            :disabled="offline"
          />
        </div>
      </div>
      <div class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">D4H Web Address</label>
        <div class="flex items-center gap-2">
          <div class="w-48">
            <FosInput v-model="subdomain" placeholder="subdomain" :disabled="offline" />
          </div>
          <span class="text-sm font-mono text-on-surface-variant truncate">.team-manager.{{ region }}.d4h.com</span>
        </div>
        <span class="text-xs text-on-surface-variant">
          Used to link FoS members to their D4H profiles. You can also paste any D4H URL.
        </span>
      </div>
      <div v-if="d4hStore.teamChoices.length > 0" class="flex flex-col gap-1.5">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">D4H Team</label>
        <FosSelect
          :model-value="selectedTeamId"
          :options="d4hStore.teamChoices.map((t) => ({ label: t.team_name, value: String(t.team_id) }))"
          placeholder="Select a team"
          @update:model-value="selectedTeamId = $event"
        />
      </div>
    </div>

    <!-- Qualification mapping -->
    <div v-if="configured" class="border border-border rounded flex flex-wrap items-center gap-3 px-3 py-2.5">
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <span class="text-[10px] font-label uppercase tracking-widest text-outline">Qualification Mapping</span>
        <span class="text-xs font-mono text-on-surface-variant">
          <template v-if="loadingQuals">Loading D4H qualifications…</template>
          <template v-else-if="d4hStore.qualifications.length">
            <span class="text-on-surface">{{ mappedCount }}</span> of {{ d4hStore.qualifications.length }} D4H qualifications mapped
          </template>
          <template v-else>
            {{ Object.keys(d4hStore.config?.qual_mapping ?? {}).length }} mapped
          </template>
        </span>
      </div>
      <FosButton variant="secondary" size="sm" :disabled="offline" @click="mappingOpen = true">Edit Mapping</FosButton>
    </div>

    <D4HQualMappingDialog v-model:open="mappingOpen" :custom-quals="customQuals" />

    <!-- Actions -->
    <div class="flex flex-wrap items-center gap-2">
      <FosButton variant="secondary" :disabled="offline || saving" @click="save">
        {{ saving ? 'Saving…' : 'Save D4H Settings' }}
      </FosButton>
      <FosButton variant="primary" :disabled="offline || !configured || d4hStore.syncing" @click="runSync">
        <span v-if="d4hStore.syncing" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white animate-spin rounded-full" />
        Sync from D4H
      </FosButton>
      <span v-if="configured" class="text-xs text-on-surface-variant font-mono ml-auto">
        Last sync {{ lastSyncLabel }}
        <template v-if="summary">
          · <span class="text-on-surface">{{ summary.total }}</span> members
          · +{{ summary.added }} / ~{{ summary.updated }} / −{{ summary.deactivated }}
          · {{ summary.linked }} linked
        </template>
      </span>
    </div>

  </div>
</template>
